import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { DeleteUserMapper } from '../../../mappers/system/user/delete-user.mapper';
import { PostUserMapper } from '../../../mappers/system/user/post-user.mapper';
import { PutUserMapper } from '../../../mappers/system/user/put-user.mapper';
import { DataResponseModel } from '../../../models/apis/data-response.model';
import { StatusResponseModel } from '../../../models/apis/status-response.model';
import { UserModel } from '../../../models/apis/system/user/user.model';
import { PostUserRequestModel } from '../../../models/apis/system/user/post-user-request.model';
import { PutUserRequestModel } from '../../../models/apis/system/user/put-user-request.model';
import { UserHttpService } from './http/user.http.service';
import { GetUsersPaginatedMapper } from '../../../mappers/system/user/get-users-paginated.mapper';
import { GetUserMapper } from '../../../mappers/system/user/get-user.mapper';
import { GetUsersPaginatedModel } from '../../../models/apis/system/user/get-users-paginated.model';
import { UserQueryModel } from '../../../models/apis/system/user/user-query.model';
import { PutUserProfileMapper } from '../../../mappers/system/user/put-user-profile.mapper';
import { PutUserProfileRequestModel } from '../../../models/apis/system/user/put-user-profile-request.model';
import { ClearSessionMapper } from '../../../mappers/system/user/clear-session.mapper';
import { PutUserLastTokenMapper } from '../../../mappers/system/user/put-user-last-token.mapper';
import { GetUserLastTokenMapper } from '../../../mappers/system/user/get-user-last-token.mapper';
import { PutUserLastTokenModel } from '../../../models/apis/system/user/put-user-last-token.model';

@Injectable({
    providedIn: 'root',
})
export class UserService {
    private readonly _userHttpService = inject(UserHttpService);
    private readonly _getUsersPaginatedMapper = inject(
        GetUsersPaginatedMapper
    );
    private readonly _getUserMapper = inject(GetUserMapper);
    private readonly _postUserMapper = inject(PostUserMapper);
    private readonly _putUserMapper = inject(PutUserMapper);
    private readonly _putUserProfileMapper = inject(PutUserProfileMapper);
    private readonly _deleteUserMapper = inject(DeleteUserMapper);
    private readonly _clearSessionMapper = inject(ClearSessionMapper);
    private readonly _putUserLastTokenMapper = inject(PutUserLastTokenMapper);
    private readonly _getUserLastTokenMapper = inject(GetUserLastTokenMapper);

    getPaginated(
        request: GetUsersPaginatedModel
    ): Observable<DataResponseModel<UserQueryModel>> {
        return this._userHttpService
            .getPaginated(this._getUsersPaginatedMapper.convertTo(request))
            .pipe(
                map((response) =>
                    this._getUsersPaginatedMapper.convertFrom(response)
                )
            );
    }

    getById(id: number): Observable<DataResponseModel<UserModel>> {
        return this._userHttpService
            .getById(id)
            .pipe(
                map((response) =>
                    this._getUserMapper.convertFrom(response)
                )
            );
    }

    getByEmail(email: string): Observable<DataResponseModel<UserModel>> {
        return this._userHttpService
            .getByEmail(email)
            .pipe(
                map((response) =>
                    this._getUserMapper.convertFrom(response)
                )
            );
    }

    getAuthenticated(): Observable<DataResponseModel<UserModel>> {
        return this._userHttpService
            .getAuthenticated()
            .pipe(
                map((response) =>
                    this._getUserMapper.convertFrom(response)
                )
            );
    }

    getUserLastToken(): Observable<DataResponseModel<string>> {
        return this._userHttpService
            .getUserLastToken()
            .pipe(
                map((response) =>
                    this._getUserLastTokenMapper.convertFrom(response)
                )
            );
    }

    post(
        request: PostUserRequestModel
    ): Observable<DataResponseModel<UserModel>> {
        return this._userHttpService
            .post(this._postUserMapper.convertTo(request))
            .pipe(
                map((response) =>
                    this._postUserMapper.convertFrom(response)
                )
            );
    }

    put(
        request: PutUserRequestModel
    ): Observable<DataResponseModel<UserModel>> {
        return this._userHttpService
            .put(this._putUserMapper.convertTo(request))
            .pipe(
                map((response) => this._putUserMapper.convertFrom(response))
            );
    }

    putUserLastToken(
        request: PutUserLastTokenModel
    ): Observable<StatusResponseModel> {
        return this._userHttpService
            .putLastToken(this._putUserLastTokenMapper.convertTo(request))
            .pipe(
                map((response) =>
                    this._putUserLastTokenMapper.convertFrom(response)
                )
            );
    }

    putProfile(
        request: PutUserProfileRequestModel
    ): Observable<DataResponseModel<UserModel>> {
        return this._userHttpService
            .putProfile(this._putUserProfileMapper.convertTo(request))
            .pipe(
                map((response) => this._putUserProfileMapper.convertFrom(response))
            );
    }

    delete(id: number): Observable<StatusResponseModel> {
        return this._userHttpService
            .delete(id)
            .pipe(
                map((response) =>
                    this._deleteUserMapper.convertFrom(response)
                )
            );
    }

    clearSession(email: string): Observable<StatusResponseModel> {
        return this._userHttpService
            .clearSession(email)
            .pipe(
                map((response) =>
                    this._clearSessionMapper.convertFrom(response)
                )
            );
    }

    putDefaultHospital(hospitalId: number): Observable<StatusResponseModel> {
        return this._userHttpService
            .putDefaultHospital({ hospitalId: hospitalId })
            .pipe(
                map((response) => ({
                    isError: response.isError,
                    errors: response.errors
                } as StatusResponseModel))
            );
    }
}
